import {useEffect, useRef} from 'react'
import {announce} from '@github-ui/aria-live'

/**
 * Announces meaningful rebase status transitions to assistive technology (WCAG
 * 2.2 SC 4.1.3). The spinner and heading changes in `RebasingStacksSection` are
 * visual-only, so without this a screen-reader user gets no feedback when the
 * rebase starts, finishes, or terminal actions become available.
 *
 * We announce once per phase — start and each terminal state — rather than on
 * every progress tick to avoid noise, and we never move focus.
 *
 * The announcement fires from an effect (after the phase has committed) rather
 * than during render. `announce` calls `document.ariaNotify`, so announcing
 * during render could fire for a render React later abandons or retries, and
 * would double-announce under Strict Mode. A ref records the last announced
 * phase so we still announce exactly once per phase transition.
 */
export function useRebaseAnnouncements({
  title,
  subtitle,
  rebaseFailed,
  isTerminal,
  failedCount,
  showResolveConflictInstructions,
}: {
  title: string
  subtitle: string
  rebaseFailed: boolean
  isTerminal: boolean
  failedCount: number
  showResolveConflictInstructions: boolean
}) {
  let phase

  if (rebaseFailed) {
    phase = 'error'
  } else if (isTerminal && failedCount === 0) {
    phase = 'complete'
  } else if (isTerminal && failedCount > 0) {
    phase = 'failed'
  } else {
    phase = 'in-progress'
  }

  const terminalActionAnnouncement = showResolveConflictInstructions
    ? 'Command-line conflict-resolution instructions are available below. Select Done to proceed.'
    : 'Select Done to proceed.'
  const announcement = isTerminal ? `${title}. ${subtitle} ${terminalActionAnnouncement}` : `${title} ${subtitle}`

  // Announce after commit, not during render: this only runs for committed
  // renders and fires exactly once per phase transition.
  const announcedPhaseRef = useRef<string | null>(null)
  useEffect(() => {
    if (announcedPhaseRef.current === phase) return
    announcedPhaseRef.current = phase
    announce(announcement)
  }, [phase, announcement])
}
